import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { CalendarHeart, MapPin, Users, Wallet, Coins, ListChecks, ArrowUpRight, Clock } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useBach } from "@/context/BachContext";
import { formatMoney, formatDate } from "@/lib/format";

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <div className="bg-white border border-[#EFE3DE] rounded-2xl p-6 shadow-sm hover:border-[#ECCDC5] transition-all" data-testid={`bach-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-widest text-[#7A686C]">{label}</span>{Icon && <Icon className="h-4 w-4 text-[#6B1D2F]" strokeWidth={1.5} />}</div>
      <div className="mt-3 text-3xl font-serif text-[#2A181C] tnum">{value}</div>
      {sub && <div className="text-sm text-[#7A686C] mt-1">{sub}</div>}
    </div>
  );
}

export default function BachDashboard() {
  const { profile } = useBach();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/bach/dashboard").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return <div className="animate-pulse space-y-4"><div className="h-32 bg-[#F5EFEA] rounded-3xl" /><div className="grid grid-cols-4 gap-4">{[0,1,2,3].map(i => <div key={i} className="h-28 bg-[#F5EFEA] rounded-2xl" />)}</div></div>;
  const cur = profile?.currency || "AUD";
  const b = d.budget, g = d.guests, t = d.tasks, p = d.payments;
  const chart = d.charts.budget_by_category.filter((c) => c.planned > 0 || c.spent > 0).slice(0, 8);

  return (
    <div>
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B1D2F] font-semibold mb-2">Let's celebrate</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2A181C] leading-tight">{profile?.party_name || "Bachelorette"}</h1>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-sm text-[#7A686C]">
          {profile?.bride_name && <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" />For {profile.bride_name}</span>}
          {profile?.date && <span className="inline-flex items-center gap-1.5"><CalendarHeart className="h-4 w-4" />{formatDate(profile.date)}</span>}
          {profile?.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{profile.location}</span>}
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="rounded-3xl bg-gradient-to-r from-[#6B1D2F] to-[#541423] text-white p-8 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 relative overflow-hidden shadow-lg">
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.2em] text-white/70">Countdown</p>
          <div className="flex items-baseline gap-3 mt-2">
            <span className="text-6xl sm:text-7xl font-serif tnum leading-none">{d.days_to_go != null ? Math.max(0, d.days_to_go) : "—"}</span>
            <span className="text-lg text-white/80">days to go</span>
          </div>
        </div>
        <div className="relative flex flex-wrap gap-3">{(profile?.palette || []).slice(0, 6).map((c, i) => <span key={i} className="h-8 w-8 rounded-full border border-white/30" style={{ background: c }} />)}</div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Stat label="Budget" value={formatMoney(b.total, cur)} sub={`${formatMoney(b.spent, cur)} spent · ${formatMoney(b.remaining, cur)} left`} icon={Wallet} />
        <Stat label="Group collected" value={formatMoney(p.collected, cur)} sub={`${formatMoney(p.outstanding, cur)} outstanding`} icon={Coins} />
        <Stat label="Guests" value={`${g.attending}/${g.invited}`} sub={`${g.rsvp_percent}% responded`} icon={Users} />
        <Stat label="Checklist" value={`${t.completed}/${t.total}`} sub={`${t.remaining} to do`} icon={ListChecks} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-[#EFE3DE] rounded-2xl p-6 shadow-sm lg:col-span-2">
          <h3 className="font-serif text-xl text-[#2A181C] mb-4">Budget by category</h3>
          {chart.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chart} margin={{ left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#7A686C" }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "#7A686C" }} />
                <Tooltip formatter={(v) => formatMoney(v, cur)} contentStyle={{ borderRadius: 12, border: "1px solid #EFE3DE" }} />
                <Bar dataKey="planned" name="Estimated" fill="#ECCDC5" radius={[4, 4, 0, 0]} />
                <Bar dataKey="spent" name="Actual" fill="#6B1D2F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-[#7A686C] py-10 text-center">Add budget categories to see this chart.</p>}
        </div>
        <div className="bg-white border border-[#EFE3DE] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4"><h3 className="font-serif text-xl text-[#2A181C]">Upcoming</h3><Link to="/bach/checklist" className="text-sm text-[#6B1D2F] inline-flex items-center gap-1 hover:underline">View <ArrowUpRight className="h-3.5 w-3.5" /></Link></div>
          {d.upcoming.length ? (
            <div className="divide-y divide-[#EFE3DE]">
              {d.upcoming.map((u, i) => (
                <div key={i} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-9 w-9 rounded-full bg-[#F9ECE8] flex items-center justify-center shrink-0">{u.type === "payment" ? <Coins className="h-4 w-4 text-[#6B1D2F]" /> : <Clock className="h-4 w-4 text-[#6B1D2F]" />}</span>
                    <div className="min-w-0"><div className="text-sm font-medium text-[#2A181C] truncate">{u.title}</div><div className="text-xs text-[#7A686C] capitalize">{u.type} · {formatDate(u.date)}</div></div>
                  </div>
                  {u.amount != null && <span className="tnum text-sm font-medium text-[#2A181C]">{formatMoney(u.amount, cur)}</span>}
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#7A686C] py-6 text-center">Nothing due soon.</p>}
        </div>
      </div>
    </div>
  );
}
